<script setup lang="ts">
	import { useHotelsStore } from '@/stores/hotels'
	import { onKeyStroke, useWindowSize } from '@vueuse/core'
	import { storeToRefs } from 'pinia'
	import { ref } from 'vue'

	import useHotels from '../composables/useHotels'
	import type { Hotel } from '../interfaces/hotel'
	import AvailabilityMapHotels from '../pages/AvailabilityMapHotels.vue'
	import HotelFiltersForm from './HotelFiltersForm.vue'
	import HotelFloatInformation from './HotelFloatInformation.vue'
	import RequestFloatingInformation from './RequestFloatingInformation.vue'
	import ViewSectionToggle from './ViewSectionToggle.vue'

	interface Props {
		isMobile: boolean
		hotelListRef: HTMLElement | null
		extraInformationIsVisible: boolean
		availabilityFormRef: any
		itemRefs: { hotelId: string; hotelContainer: HTMLElement | null }[]
		// Alto medido en vivo (no fijo) de todo lo que vive arriba del mapa en
		// AvailabilityPage.vue (breadcrumb, form, barra de búsqueda colapsada).
		// Ver el comentario junto a aboveMapRef ahí para el porqué.
		aboveMapHeight: number
	}

	interface Emits {
		(e: 'open-disclaimer-modal', disclaimer: string): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()

	const displayMap = defineModel<boolean>('displayMap', { required: true })
	const displayHotels = defineModel<boolean>('displayHotels', {
		required: true,
	})

	const mapRef = ref<InstanceType<typeof AvailabilityMapHotels> | null>(null)

	const { form, selectedHotel, reservationNights, pushToDetails } = useHotels()

	const centerActiveHotel = (hotel: Hotel) => {
		mapRef.value?.centerActiveHotel(hotel)
	}

	defineExpose({
		centerActiveHotel,
	})

	const { width } = useWindowSize()

	const isMapTipMinimized = ref(false)

	// Filtros también accesibles con el mapa abierto:
	// en mobile displayHotels pasa a false y esconde por completo el botón
	// "Filtros" de AvailabilityFilters.vue (vive en la columna de lista, que
	// se oculta) — sin esto no había forma de filtrar sin salir del mapa.
	const { activeFilterCount, searchContext } = storeToRefs(useHotelsStore())
	const showMapFilters = ref(false)
	const onFilterEscape = (event: KeyboardEvent) => {
		if (!showMapFilters.value) return
		event.stopPropagation()
		showMapFilters.value = false
	}

	const moveToBottom = () => {
		const element = document.body

		element.scrollIntoView({ block: 'end', behavior: 'smooth' })
	}

	const toggleSections = () => {
		displayHotels.value = !displayHotels.value
		displayMap.value = !displayMap.value

		if (displayMap.value) {
			setTimeout(() => {
				moveToBottom()
			}, 200)
		}
	}

	const closeMap = () => {
		displayMap.value = false
		displayHotels.value = true
	}

	// Escape primero cierra lo que esté encima (pastilla de filtro, menú de orden):
	// reka-ui no detiene el evento, y sin esta guarda cerraba también el mapa.
	onKeyStroke('Escape', (event) => {
		const target = event.target instanceof Element ? event.target : null
		const fromOverlay = target?.closest(
			'[data-reka-popper-content-wrapper], [role="menu"], [role="dialog"], [aria-expanded="true"]'
		)
		if (fromOverlay) return

		// La tarjeta del hotel elegido es lo que está encima del mapa: se cierra primero.
		if (selectedHotel.value?.id) {
			mapRef.value?.closeInfoWindow()
			return
		}

		closeMap()
	})

	const scrollToTargetAdjusted = (element: HTMLElement | null) => {
		if (element) {
			var headerOffset = 64
			var elementPosition = element.getBoundingClientRect().top
			var offsetPosition = elementPosition + window.scrollY - headerOffset

			window.scrollTo({
				top: offsetPosition,
				left: 0,
				behavior: 'smooth',
			})
		}
	}

	const moveToList = (element: HTMLElement | null) => {
		if (element) {
			scrollToTargetAdjusted(element)
		}
	}
</script>

<template>
	<!--
		100dvh (no 100vh) - 5rem (header fijo de la app) - aboveMapHeight (medido
		en vivo en AvailabilityPage.vue: breadcrumb + form + barra de búsqueda
		colapsada). Antes esto último era un número fijo (130px) — el documento
		quedaba más alto que el viewport apenas ese contenido medía algo distinto
		(el banner de políticas infringidas, un breakpoint distinto, etc.) y
		aparecía un scroll de página fantasma con el mapa abierto, aunque nada
		scrolleable hiciera falta. 100vh en mobile (Chrome/Safari) mide el
		viewport MÁS GRANDE (con la barra de direcciones colapsada) — con la
		barra visible el contenido real es más chico, así que 100vh siempre se
		pasa y el body desborda. 100dvh sigue el viewport visible real.
	-->
	<div
		class="sticky top-40 bottom-0"
		:style="{ height: `calc(100dvh - 5rem - ${aboveMapHeight}px)` }"
	>
		<!--
			absolute (no fixed): relativo al propio contenedor del mapa, no al
			viewport. Con el overflow de página ya resuelto arriba, top-4 vuelve a
			ser una posición segura — el contenedor del mapa ya no arranca tapado
			por la barra de filtros ni depende de la altura de la ventana del
			usuario.
		-->
		<button
			v-if="displayHotels"
			type="button"
			aria-label="Cerrar mapa"
			class="absolute top-4 right-4 z-20 flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg shadow-lg transition-all duration-200 hover:scale-105 font-semibold"
			@click="closeMap"
			v-tooltip="'Cerrar mapa'"
		>
			<span class="material-symbols-outlined">close</span>
			<span class="hidden md:inline">Cerrar Mapa</span>
		</button>

		<!-- View hotels button tablet -->
		<button
			v-if="!displayHotels && width >= 768"
			@click="toggleSections"
			class="absolute top-4 right-4 z-20 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg transition-all duration-200 font-semibold"
		>
			<span class="material-symbols-outlined">list</span>
			<span>Ver Hoteles</span>
		</button>

		<!-- Close map button on mobile -->
		<ViewSectionToggle
			v-if="!displayHotels && width < 768"
			text="Ver hoteles"
			:toggle-sections="toggleSections"
		/>

		<!-- Filtros sobre el mapa: mismo panel que en el listado (HotelFiltersForm
		     lee/escribe directo del store), para no depender de la columna de
		     lista, que en mobile desaparece con el mapa abierto. Apilado bajo
		     Cerrar Mapa/Ver Hoteles (misma esquina, mismo ancho) en vez de
		     compartir columna con el aviso de "Hoteles alrededor de" — así
		     ninguno necesita un offset a mano para no encimarse con el otro.
		     En móvil, Ver hoteles vive abajo y Filtros ocupa la esquina superior libre,
		     lejos del riel de contactos que queda a media altura. -->
		<button
			type="button"
			aria-label="Filtros del mapa"
			:aria-expanded="showMapFilters"
			@click="showMapFilters = !showMapFilters"
			@keydown.esc="onFilterEscape"
			:class="[
				'absolute right-4 z-20 flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg transition-all duration-200 hover:scale-105',
				displayHotels ? 'top-16' : 'top-4 md:top-16',
				showMapFilters || activeFilterCount > 0 ?
					'bg-[#021F58] text-white hover:bg-[#1C3F7A]'
				:	'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
			]"
		>
			<span class="material-symbols-outlined text-lg">tune</span>
			<span class="hidden md:inline">Filtros</span>
			<span
				v-if="activeFilterCount > 0"
				class="inline-flex items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-bold"
				:class="!showMapFilters && 'bg-[#021F58] text-white'"
			>
				{{ activeFilterCount }}
			</span>
		</button>

		<Transition
			enter-active-class="transition-all duration-200 ease-out"
			enter-from-class="opacity-0 -translate-y-1"
			enter-to-class="opacity-100 translate-y-0"
			leave-active-class="transition-all duration-150 ease-in"
			leave-from-class="opacity-100 translate-y-0"
			leave-to-class="opacity-0 -translate-y-1"
		>
			<div
				v-if="showMapFilters"
				class="absolute top-28 right-4 z-20 max-h-[70vh] w-[calc(100vw-2rem)] max-w-sm overflow-y-auto"
			>
				<HotelFiltersForm />
			</div>
		</Transition>

		<!-- Aviso de ubicación + tip de "buscar en el mapa": una sola columna en
		     flujo normal (gap fijo, sin offsets a mano) para que el tip siempre
		     quede pegado justo debajo del aviso sin impotar su alto real.
		     max-w-xs solo en el tip (texto corto, fijo) — el aviso de ubicación
		     lo dejamos sin ese tope: su título crece con md:text-xl y con
		     max-w-xs compartido se partía a media palabra. -->
		<div
			class="absolute top-4 left-4 z-[1] flex max-w-[calc(100%-6rem)] flex-col items-start gap-2 md:max-w-[calc(100%-12rem)]"
		>
			<RequestFloatingInformation
				:form="form"
				:search-context="searchContext"
			/>

			<!-- Alerta expandida -->
			<Transition name="fade">
				<div
					v-if="!isMapTipMinimized"
					role="note"
					aria-label="Ayuda para buscar en el mapa"
					class="w-full max-w-xs bg-blue-600 text-white px-4 py-3 rounded-lg shadow-md relative"
				>
					<!-- Botón minimizar -->
					<button
						@click="isMapTipMinimized = true"
						class="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-100 transition-colors"
						v-tooltip="'Minimizar'"
					>
						<span class="material-symbols-outlined text-gray-600 text-sm">
							remove
						</span>
					</button>

					<div class="flex items-start gap-3">
						<span class="material-symbols-outlined text-2xl animate-pulse"
							>touch_app</span
						>
						<div class="min-w-0 flex-1 break-words">
							<p class="font-bold text-sm mb-2">Buscar en el mapa</p>
							<p class="text-xs leading-tight">
								<template v-if="searchContext?.radiusKm">
									La búsqueda actual cubre un radio de
									<span class="font-bold">{{ searchContext.radiusKm }} km</span
									>.
								</template>
								Haz clic en otro punto para buscar en esa ubicación.
							</p>
						</div>
					</div>
					<div
						class="mt-2 flex items-center gap-2 text-xs bg-white/20 rounded px-2 py-1"
					>
						<span class="material-symbols-outlined text-sm">circle</span>
						<span>El círculo azul muestra el área de búsqueda</span>
					</div>
				</div>
			</Transition>

			<!-- Alerta minimizada -->
			<Transition name="fade">
				<button
					v-if="isMapTipMinimized"
					@click="isMapTipMinimized = false"
					class="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-full shadow-md hover:scale-105 transition-transform"
					v-tooltip="'Ver instrucciones'"
				>
					<span class="material-symbols-outlined text-lg animate-pulse"
						>touch_app</span
					>
					<span class="text-xs font-semibold">Buscar en mapa</span>
					<span class="material-symbols-outlined text-sm">expand_more</span>
				</button>
			</Transition>
		</div>

		<!-- Map scroll to top button -->
		<button
			v-if="displayHotels && !extraInformationIsVisible"
			class="absolute bottom-4 left-4 z-[1] bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 border-2 border-gray-300"
			@click="() => moveToList(hotelListRef)"
			v-tooltip="'Volver arriba'"
		>
			<span class="material-symbols-outlined text-gray-700">arrow_upward</span>
		</button>

		<!-- Hotel float information -->
		<HotelFloatInformation
			v-if="displayMap && selectedHotel?.id"
			:display-hotels="displayHotels"
			:selected-hotel="selectedHotel"
			:reservation-nights="reservationNights"
			:close-info-window="() => mapRef?.closeInfoWindow()"
			:open-disclaimer-modal="
				(disclaimer: string) => emits('open-disclaimer-modal', disclaimer)
			"
			@push-to-details="pushToDetails"
		/>

		<AvailabilityMapHotels
			ref="mapRef"
			:availability-form-ref="props.availabilityFormRef"
			:display-map="displayMap"
			:show-radius-label="false"
			:item-refs="props.itemRefs"
		/>
	</div>
</template>

<style scoped>
	.fade-enter-active,
	.fade-leave-active {
		transition: all 0.3s ease;
	}

	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
		transform: scale(0.95);
	}
</style>
